import { isRouteErrorResponse, useRouteError } from "react-router";
import { Feedback } from "../feedback";
import { Button } from "@mindstonehq/ui";
import { useEffect } from "react";
import React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

export declare class ErrorResponse {
  status: number;
  statusText: string;
  data: any;
  error?: Error;
  internal: boolean;
  constructor(
    status: number,
    statusText: string | undefined,
    data: any,
    internal?: boolean,
  );
}

interface GenericBoundaryProps {
  /**
   * Custom link component for navigation (e.g., react-router Link)
   */
  LinkComponent?: React.ComponentType<any>;
  /**
   * Optional Sentry instance for error reporting
   */
  Sentry?: any;
}

export function GenericBoundary({
  LinkComponent,
  Sentry,
}: GenericBoundaryProps) {
  const error = useRouteError();

  useEffect(() => {
    if (Sentry) {
      Sentry.captureException(error);
    }
  }, [error, Sentry]);

  return (
    <>
      {isRouteErrorResponse(error) ? (
        <GenericCatchBoundary
          error={error}
          LinkComponent={LinkComponent}
          Sentry={Sentry}
        />
      ) : (
        <GenericErrorBoundary
          error={error instanceof Error ? error : new Error("Unknown Error")}
          LinkComponent={LinkComponent}
          Sentry={Sentry}
        />
      )}
    </>
  );
}

interface ErrorBoundaryProps {
  error: Error;
  LinkComponent?: React.ComponentType<any>;
  Sentry?: any;
}

export function GenericErrorBoundary({
  error,
  LinkComponent,
}: ErrorBoundaryProps) {
  const { t } = useLingui();
  const errorTitle = t(msg`The AI broke free again. Classic.`);

  if ((error?.message || "").toLowerCase().includes("not_found")) {
    return (
      <Feedback
        code={404}
        title={errorTitle}
        subtitle={t(
          msg`We can't find that page. The link may have moved or expired. Double-check the URL or head back home.`,
        )}
      />
    );
  }
  if ((error?.message || "").toLowerCase().includes("unauthorized")) {
    const loginCta = LinkComponent ? (
      <LinkComponent to="/login">
        <Button className="min-w-[132px]" variant="secondary">
          {t(msg`Log in`)}
        </Button>
      </LinkComponent>
    ) : (
      <a href="/login">
        <Button className="min-w-[132px]" variant="secondary">
          {t(msg`Log in`)}
        </Button>
      </a>
    );

    return (
      <Feedback
        code={401}
        title={errorTitle}
        subtitle={t(msg`Log in to access this page or create an account.`)}
        cta={loginCta}
      />
    );
  }
  if ((error?.message || "").toLowerCase().includes("forbidden")) {
    return (
      <Feedback
        code={403}
        title={errorTitle}
        subtitle={t(
          msg`You don't have permission to view this. Try a different account or space, or contact support if you believe this is an error.`,
        )}
      />
    );
  }
  return (
    <Feedback
      code={500}
      title={errorTitle}
      subtitle={t(
        msg`Something went wrong on our side. We're on it - please try again shortly or contact support if the problem persists.`,
      )}
    />
  );
}

interface CatchBoundaryProps {
  error: { status: number };
  LinkComponent?: React.ComponentType<any>;
  Sentry?: any;
}

export function GenericCatchBoundary({ error, Sentry }: CatchBoundaryProps) {
  const { t } = useLingui();
  const errorTitle = t(msg`The AI broke free again. Classic.`);

  if (error.status === 404) {
    return (
      <Feedback
        code={404}
        title={errorTitle}
        subtitle={t(
          msg`We can't find that page. The link may have moved or expired. Double-check the URL or head back home.`,
        )}
      />
    );
  }

  if (error.status === 403) {
    return (
      <Feedback
        code={403}
        title={errorTitle}
        subtitle={t(
          msg`This resource is not accessible by anyone other than its owner.
          \nPlease ask them for access.`,
        )}
      />
    );
  }

  return (
    <Feedback
      code={error.status}
      title={errorTitle}
      subtitle={t(
        msg`We've logged the issue. Retry in a moment or reach out if it persists.`,
      )}
    />
  );
}
